import { useMemo, useRef } from "react";
import { toast } from "sonner";

import { useModal } from "@/contexts/ModalContext";
import { useAssetsList } from "@/contexts/AssetsListContext";
import ConfirmModal from "@/components/modals/ConfirmModal";
import AssetsListLoaderModal from "@/components/modals/AssetsListLoaderModal";
import AssetsListSaveAsModal from "@/components/modals/AssetsListSaveAsModal";
import AssetsListLayout from "./AssetsListLayout";
import { resolveEntryAsset } from "./resolveEntryAsset";
import { AssetsListSaveOutcome } from "./constants";

// Clicking a box opens AssetsListAssetPreview, an overlay rendered alongside
// this strip rather than a modal owned by it — so none of the grid's asset
// actions (archive, move, destroy) need forwarding in here any more.
export default function AssetsListContainer({ resolveAssetById }) {
  const { openModal } = useModal();
  const {
    workingList,
    isExpanded,
    isSaving,
    isLoading,
    setExpanded,
    insertAssetsAt,
    moveEntry,
    removeEntry,
    startNewList,
    loadList,
    saveList,
    saveListAs,
    deleteList,
    renameWorkingList,
    previewEntryKey,
    openEntryPreview,
    downloadList,
    canDownloadList,
    isDownloadingList,
  } = useAssetsList();

  const isModalFlowOpenRef = useRef(false);

  const boxes = useMemo(
    () =>
      workingList.entries.map((entry) => ({
        entryKey: entry.entryKey,
        assetId: entry.asset_id,
        asset: resolveEntryAsset({
          assetId: entry.asset_id,
          resolveAssetById,
          assetsById: workingList.assetsById,
        }),
        isPreviewing: entry.entryKey === previewEntryKey,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      workingList.entries,
      workingList.assetsById,
      resolveAssetById,
      previewEntryKey,
    ],
  );

  const handleToggleExpanded = () => setExpanded({ expanded: !isExpanded });

  const handleExpandForDrag = () => setExpanded({ expanded: true });

  const handleInsertAssetsAt = ({ index, assets }) =>
    insertAssetsAt({ index, assets });

  const handleMoveEntry = ({ fromIndex, toIndex }) =>
    moveEntry({ fromIndex, toIndex });

  const handleRemoveEntry = ({ entryKey }) => removeEntry({ entryKey });

  const handleRenameList = ({ name }) => renameWorkingList({ name });

  const handleNewList = async () => {
    if (!workingList.isDirty) {
      startNewList();
      return;
    }

    const confirmed = await openModal(
      <ConfirmModal
        title="Start a new list?"
        message="The current list has unsaved changes. Starting a new one discards them."
        type="warning"
      />,
    );

    if (!confirmed) return;
    startNewList();
  };

  // Opens the naming modal and creates a new saved list from it. Returns the
  // freshly saved snapshot, or null if the modal was cancelled or the save failed.
  const promptSaveAs = async () => {
    const chosen = await openModal(
      <AssetsListSaveAsModal
        initialName={workingList.name}
        initialVisibility={workingList.visibility}
      />,
    );

    if (!chosen) return null;

    const { snapshot } = await saveListAs({
      name: chosen.name,
      visibility: chosen.visibility,
    });
    toast.success(`Saved "${chosen.name}"`);
    return snapshot;
  };

  const handleSaveListAs = async () => {
    try {
      await promptSaveAs();
    } catch {
      // Already toasted by the working list's handler.
    }
  };

  // Saves in place when possible, otherwise falls through to the Save As
  // prompt. Returns the freshly saved snapshot, or null if saving was
  // cancelled or failed — a caller that needs the list to now be downloadable
  // (the download button's save-first prompt) reads that off the return value
  // rather than off `workingList`, which won't reflect the save until React
  // flushes the state update.
  const ensureListSaved = async () => {
    try {
      const { outcome, snapshot } = await saveList();

      if (outcome === AssetsListSaveOutcome.RequiresSaveAs) {
        return await promptSaveAs();
      }

      toast.success("List saved");
      return snapshot;
    } catch {
      // Already toasted by the working list's handler.
      return null;
    }
  };

  const handleSaveList = async () => {
    await ensureListSaved();
  };

  const handleDownloadList = async () => {
    if (boxes.length === 0) return;

    if (canDownloadList) {
      downloadList();
      return;
    }

    const confirmed = await openModal(
      <ConfirmModal
        title="Save before downloading?"
        message="The download packages the saved list with its edit decision list, so the list needs to be saved first."
        type="warning"
      />,
    );
    if (!confirmed) return;

    const snapshot = await ensureListSaved();
    if (!snapshot) return;

    downloadList({ snapshot });
  };

  const runModalFlowOnce = async (modalFlow) => {
    if (isModalFlowOpenRef.current) return;

    isModalFlowOpenRef.current = true;
    try {
      await modalFlow();
    } finally {
      isModalFlowOpenRef.current = false;
    }
  };

  const handleDeleteListFromPicker = ({ assetsListId }) =>
    deleteList({ assetsListId });

  const handleLoadList = () =>
    runModalFlowOnce(async () => {
      if (workingList.isDirty) {
        const confirmed = await openModal(
          <ConfirmModal
            title="Load another list?"
            message="The current list has unsaved changes. Loading another one discards them."
            type="warning"
          />,
        );
        if (!confirmed) return;
      }

      const chosenList = await openModal(
        <AssetsListLoaderModal onDeleteList={handleDeleteListFromPicker} />,
      );
      if (!chosenList) return;

      try {
        await loadList({ assetsListId: chosenList._id });
      } catch {
        // Already toasted by the working list's handler.
      }
    });

  const handleDeleteList = () =>
    runModalFlowOnce(async () => {
      const listName = workingList.name || "Untitled list";
      const unsavedWarning = workingList.isDirty
        ? " Unsaved changes are discarded too."
        : "";

      const confirmed = await openModal(
        <ConfirmModal
          title="Delete this list?"
          message={`"${listName}" will be deleted for everyone on this show. This cannot be undone.${unsavedWarning}`}
          type="destructive"
        />,
      );
      if (!confirmed) return;

      try {
        /** @throws AssetsListApiError — already toasted by the provider */
        await deleteList({ assetsListId: workingList.assetsListId });
        toast.success("List deleted");
      } catch {
        // Already toasted by the working list's handler.
      }
    });

  return (
    <AssetsListLayout
      listName={workingList.name}
      hasLoadedList={Boolean(workingList.assetsListId)}
      boxes={boxes}
      isDirty={workingList.isDirty}
      isExpanded={isExpanded}
      isBusy={isSaving || isLoading}
      isDownloadingList={isDownloadingList}
      resolveAssetById={resolveAssetById}
      onToggleExpanded={handleToggleExpanded}
      onExpandForDrag={handleExpandForDrag}
      onNewList={handleNewList}
      onSaveList={handleSaveList}
      onSaveListAs={handleSaveListAs}
      onRenameList={handleRenameList}
      onDownloadList={handleDownloadList}
      onLoadList={handleLoadList}
      onDeleteList={handleDeleteList}
      onInsertAssetsAt={handleInsertAssetsAt}
      onMoveEntry={handleMoveEntry}
      onRemoveEntry={handleRemoveEntry}
      onOpenEntryPreview={openEntryPreview}
    />
  );
}
